import { useEffect, useState } from "react";
import { Stack, Tooltip } from "@mui/material";
import {
  FunctionField,
  DatagridConfigurable,
  List,
  NumberField,
  SearchInput,
  TextField,
  DateField,
  FilterLiveSearch,
  NumberInput,
  BooleanField,
  Button,
  useListContext,
  useCreate,
  useNotify,
  TextInput,
  AutocompleteInput,
  ReferenceArrayInput,
  SelectArrayInput,
  ReferenceInput,
  SelectInput,
  useGetList,
  CreateButton,
  TopToolbar,
  FilterButton,
  AutocompleteArrayInput,
} from "react-admin";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import { Chip } from "@mui/material";
import { ScmSkus } from "../types";
import CustomPagination from "../Componentes/CustomPagination";
import { ScmSkusEmpty } from "./ScmSkusEmpty";

// Tipos
interface QuickFilterProps {
  label: string;
  source?: string;
  defaultValue?: boolean;
}

const QuickFilter: React.FC<QuickFilterProps> = ({ label, defaultValue }) => (
  <Chip sx={{ marginBottom: 1 }} label={label} />
);

const LabelWithTooltip = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Tooltip title={title} placement="top" enterDelay={300}>
    <span>{children}</span>
  </Tooltip>
);

const scmSkuRowSx = (record: ScmSkus & { sku_ativo?: boolean }) =>
  record.sku_ativo === true ? {} : { backgroundColor: "lightgray" };

const optionSkuGroupsRenderer = (choice: any) =>
  `${choice.codigo_agrup}. ${choice.desc_agrupamento}`;

const optionFornecedorRenderer = (choice: any) =>
  `${choice?.nome_fornec} ${typeof choice?.quant_skus !== "undefined" ? "(" + choice?.quant_skus + " SKUs)" : ""}`;

const optionCategoriesRenderer = (choice: any) => {
  return choice
    ? `${choice.id !== null ? choice.id + ". " : ""} ${
        typeof choice.breadcrumb !== "undefined" ? choice.breadcrumb : ""
      }`
    : "";
};

const filters = [
  <FilterLiveSearch source="q" label="Cód Produto/Descrição" alwaysOn />,
  <ReferenceArrayInput
    source="ids_tags"
    reference="producttags"
    alwaysOn
    sort={{ field: "nome_tag", order: "ASC" }}
    perPage={100}
  >
    <AutocompleteArrayInput
      label="Tags"
      // optionText="nome_fornec"
      optionText="nome_tag"
      filterToQuery={(nome_tag) => ({ nome_tag })}
      // helperText={false}
      // sx={{ width: "400px" }}
    />
  </ReferenceArrayInput>,
  <ReferenceInput source="id_fornec" reference="suppliers" alwaysOn>
    <AutocompleteInput
      label="Fornecedor"
      // optionText="nome_fornec"
      optionText={optionFornecedorRenderer}
      filterToQuery={(pesquisa) => ({ pesquisa })}
      // helperText={false}
      sx={{ width: "400px" }}
    />
  </ReferenceInput>,
  // #####################################
  <ReferenceArrayInput
    resource="products"
    source="ids_acs_excluir"
    label="ACs a Excluir"
    reference="skugroups"
    perPage={100}
    sort={{ field: "id", order: "ASC" }}
    InputLabelProps={{ shrink: true }}
  >
    <SelectArrayInput
      optionText={optionSkuGroupsRenderer}
      style={{ width: "250px" }}
    />
  </ReferenceArrayInput>,
  <ReferenceInput
    source="id_comprador"
    reference="buyers"
    perPage={100}
    sort={{ field: "id", order: "ASC" }}
    fullWidth
    label="Comprador"
  >
    <SelectInput optionText="nome_razao" label="Comprador" />
  </ReferenceInput>,
  <NumberInput source="curva_minima" label="Curva Min" />,
  <NumberInput source="curva_maxima" label="Curva Max" />,
  <NumberInput source="id" label="ID" />,
  <QuickFilter
    label="Incluir Inativos"
    source="incluir_inativos"
    defaultValue={true}
  />,
  <NumberInput source="numero_pedido" label="Pedido" />,
  // <TextInput source="taxonomia" />,
  <ReferenceInput
    label="Taxonomia"
    reference="categories"
    resource="products"
    source="id_categoria"
    style={{ width: "450px" }}
    perPage={9999}
    sort={{ field: "breadcrumb", order: "ASC" }}
    //fullWidth
    // filter={{ dump: true }}
    resettable
  >
    <AutocompleteInput
      source="id"
      optionText={optionCategoriesRenderer}
      label="Taxonomia"
      style={{ width: "450px" }}
    />
  </ReferenceInput>,
];

const ScmSkusActions = () => {
  return (
    <TopToolbar>
      <FilterButton disableSaveQuery />
    </TopToolbar>
  );
};

const CriaAnalise = () => {
  const { selectedIds, filterValues } = useListContext();
  const [create] = useCreate();
  const notify = useNotify();

  // console.log("filterValues", filterValues);

  const BtnEditaLote = (selectedIds: any) => {
    if (!selectedIds.length) {
      notify("Nenhum item selecionado.", { type: "warning" });
      return;
    }

    const payload = {
      skus: selectedIds,
      // desc_analise: JSON.stringify(filterValues),
      //   desc_analise: JSON.stringify(filterValues).replace(/^{|}$/g, ""),
      filtros_analise: filterValues,
    };

    create(
      "scmanalyses", // Nome do recurso no backend
      { data: payload },
      {
        onSuccess: (response: any) => {
          // console.log("response", response);
          notify("Análise criada com sucesso!", { type: "success" });
          // Redirecionamento opcional após o sucesso
          window.location.href = `#/scmanalyses/${response?.id}`;
        },
        onError: () => {
          notify("Erro ao criar a análise.", { type: "error" });
        },
      },
    );

    localStorage.setItem("ids", JSON.stringify(selectedIds));
    localStorage.setItem("filters", JSON.stringify(filterValues));
  };

  return (
    <Button
      size="small"
      color="primary"
      onClick={() => BtnEditaLote(selectedIds)}
      label="Criar Análise"
    >
      <AnalyticsIcon />
    </Button>
  );
};

export function ScmSkusList(props: any) {
  // Para controlar o filtro default da tela de compras nos tags
  const { data } = useGetList("producttags", {
    filter: { padrao_compra: true },
    pagination: { perPage: 9999, page: 1 },
    sort: { field: "nome_tag", order: "ASC" },
    // pagination:{perPage:  }
  });
  const [selectedIds, setSelectedIds] = useState<any[]>([]);
  useEffect(() => {
    if (data && selectedIds.length === 0) {
      const defaultIds = data.map((item) => item.id); // Define os valores iniciais com base nos dados
      setSelectedIds(defaultIds); // Atualiza o estado com os IDs padrão
    }
  }, [data]);

  return (
    <Stack gap={4}>
      <List
        // {...props}
        storeKey={false}
        disableSyncWithLocation
        filters={filters}
        actions={<ScmSkusActions />}
        sort={{ field: "prod_xprod", order: "ASC" }}
        perPage={50}
        filterDefaultValues={{
          ids_tags: selectedIds,
        }}
        pagination={<CustomPagination />}
        empty={false}
      >
        <DatagridConfigurable
          bulkActionButtons={<CriaAnalise />}
          rowSx={scmSkuRowSx}
          empty={<ScmSkusEmpty />}
        >
          <NumberField
            source="id"
            label="ID"
            options={{ useGrouping: false }}
          />
          <TextField source="referencia" label="Referência" />
          <TextField source="ucom" label="Un." />
          <FunctionField<ScmSkus>
            label="Produto/Taxonomia"
            sortBy="id_categoria"
            render={(record) => (
              <div>
                <TextField source="prod_xprod" />
                <div style={{ fontSize: "smaller" }}>
                  <NumberField
                    source="id_categoria"
                    options={{ useGrouping: false }}
                    sx={{ fontSize: "x-small" }}
                  />
                  {record?.taxonomia ? ". " : ""}
                  <TextField source="taxonomia" sx={{ fontSize: "x-small" }} />
                </div>
              </div>
            )}
          />
          <FunctionField<ScmSkus>
            label="Fornecedor/Comprador"
            sortBy="nome_fornec"
            render={(record) => (
              <div>
                <NumberField
                  source="id_fornec"
                  options={{ useGrouping: false }}
                />
                {". "}
                <TextField source="nome_fornec" />
                <div style={{ fontSize: "smaller" }}>
                  <TextField
                    source="nome_comprador"
                    sx={{ fontSize: "x-small" }}
                  />
                </div>
              </div>
            )}
          />
          <TextField source="nome_marca" label="Marca" />
          <TextField
            source="codigo_agrup"
            label={
              <LabelWithTooltip title="Argumento Comercial">
                AC
              </LabelWithTooltip>
            }
          />
          <NumberField
            source="cmm_total"
            label="CMM Total"
            options={{ useGrouping: false }}
          />
          <DateField source="ultima_compra" label="Ult. Compra" />
          <NumberField
            source="vunit_compra"
            options={{
              style: "currency",
              currency: "BRL",
            }}
            label="V.Unit Compra"
          />
          {/* <BooleanField source="sku_ativo" label="SKU Ativo" /> */}
        </DatagridConfigurable>
      </List>
    </Stack>
  );
}
